/**
 * BillingPanel — pricing plans display within user settings.
 * Shows Lead Generation, E-commerce, and Content Creation verticals
 * with Start / Pro / Expert tiers per vertical.
 * Supports plan stacking — select one plan per vertical, checkout all at once.
 */
import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useSpring } from "framer-motion";
import confetti from "canvas-confetti";
import NumberFlow from "@number-flow/react";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { getFirstPromoterTid } from "@/lib/firstpromoter";
import {
  Check,
  Sparkles,
  ShoppingCart,
  Film,
  Zap,
  Crown,
  Star,
  Loader2,
  Settings,
  X,
  ArrowRight,
  MonitorCheck,
  AlertTriangle,
  Gift,
  Plus,
  BarChart3,
  Bot,
  Megaphone,
  Video,
  FileText,
  Lightbulb,
  Radar,
  Cpu,
  Users,
  Building2,
  Coins,
  Clapperboard,
  ImageIcon,
  Pause,
  MessageSquare,
  Headphones,
  Calendar,
  TrendingDown,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import PlanFinder from "@/components/plan-finder";
import TokenPackPurchase from "@/components/token-pack-purchase";
import type { PlanVertical } from "@shared/plan-vertical";

// ═══════════════════════════════════════════════════════
// Interactive Starfield
// ═══════════════════════════════════════════════════════

function StarParticle({
  mousePosition,
  containerRef,
}: {
  mousePosition: { x: number | null; y: number | null };
  containerRef: React.RefObject<HTMLDivElement>;
}) {
  const [initialPos] = useState({
    top: `${Math.random() * 100}%`,
    left: `${Math.random() * 100}%`,
  });
  const [size] = useState(1 + Math.random() * 1.5);
  const [duration] = useState(2 + Math.random() * 3);
  const [delay] = useState(Math.random() * 5);

  const springConfig = { stiffness: 100, damping: 15, mass: 0.1 };
  const springX = useSpring(0, springConfig);
  const springY = useSpring(0, springConfig);

  useEffect(() => {
    if (!containerRef.current || mousePosition.x === null || mousePosition.y === null) {
      springX.set(0);
      springY.set(0);
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const starX = rect.left + (parseFloat(initialPos.left) / 100) * rect.width;
    const starY = rect.top + (parseFloat(initialPos.top) / 100) * rect.height;
    const dx = mousePosition.x - starX;
    const dy = mousePosition.y - starY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const radius = 500;
    if (dist < radius) {
      const force = 1 - dist / radius;
      springX.set(dx * force * 0.4);
      springY.set(dy * force * 0.4);
    } else {
      springX.set(0);
      springY.set(0);
    }
  }, [mousePosition, initialPos, containerRef, springX, springY]);

  return (
    <motion.div
      className="absolute rounded-full bg-white/50"
      style={{
        top: initialPos.top,
        left: initialPos.left,
        width: size,
        height: size,
        x: springX,
        y: springY,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 0.7, 0] }}
      transition={{ duration, repeat: Infinity, delay }}
    />
  );
}

function InteractiveStarfield({
  mousePosition,
  containerRef,
}: {
  mousePosition: { x: number | null; y: number | null };
  containerRef: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none">
      {Array.from({ length: 120 }).map((_, i) => (
        <StarParticle key={i} mousePosition={mousePosition} containerRef={containerRef} />
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════

// VP-116: jedno źródło prawdy — patrz packages/shared/plan-vertical.ts.
type Vertical = PlanVertical;
type Tier = "start" | "pro" | "expert";

interface PlanFeature {
  text: string;
  featureKey: string;
  included: boolean;
}

interface PricingTier {
  tier: Tier;
  name: string;
  subtitle: string;
  price: number;
  bestSeller?: boolean;
  tokens: number;
  users: number;
  features: PlanFeature[];
}

interface VerticalConfig {
  id: Vertical;
  name: string;
  description: string;
  icon: React.ReactNode;
  tiers: PricingTier[];
  comingSoon?: boolean;
}

// ═══════════════════════════════════════════════════════
// Plan definitions
// ═══════════════════════════════════════════════════════

export const VERTICALS: VerticalConfig[] = [
  {
    id: "lead_generation",
    name: "Lead Generation",
    description: "Pozyskiwanie leadów z Meta/Google Ads",
    icon: <Zap className="w-5 h-5" />,
    tiers: [
      {
        tier: "start",
        name: "Start",
        subtitle: "Skuteczne podstawy lead generation",
        price: 499,
        tokens: 2000,
        users: 1,
        features: [
          { text: "Twórca reklam (AI copy)", featureKey: "ad_creator_ai_copy", included: true },
          { text: "Twórca ofert", featureKey: "offer_creator", included: true },
          { text: "1 integracja reklamowa", featureKey: "ad_integration_1", included: true },
          { text: "Wsparcie email", featureKey: "email_support", included: true },
          { text: "Twórca VSL", featureKey: "vsl_creator", included: false },
          { text: "Analityk AI", featureKey: "ai_analyst", included: false },
          { text: "Tracking & atrybucja", featureKey: "tracking_attribution", included: false },
          {
            text: "CRM (LeadConnector / HubSpot)",
            featureKey: "crm_lead_hubspot",
            included: false,
          },
        ],
      },
      {
        tier: "pro",
        name: "Pro",
        subtitle: "Pełna moc AI i inteligentna analityka",
        price: 1499,
        bestSeller: true,
        tokens: 6000,
        users: 3,
        features: [
          { text: "Wszystkie funkcje z Planu Start", featureKey: "all_from_start", included: true },
          { text: "Twórca VSL", featureKey: "vsl_creator", included: true },
          {
            text: "Analityk AI (rekomendacje)",
            featureKey: "ai_analyst_recommendations",
            included: true,
          },
          { text: "Tracking podstawowy", featureKey: "tracking_basic", included: true },
          { text: "Meta + Google Ads", featureKey: "meta_google_ads", included: true },
          { text: "CRM (LeadConnector / HubSpot)", featureKey: "crm_lead_hubspot", included: true },
          { text: "Priorytetowe wsparcie", featureKey: "priority_support", included: true },
          { text: "Panel agencyjny", featureKey: "agency_panel", included: false },
        ],
      },
      {
        tier: "expert",
        name: "Expert",
        subtitle: "Pełna automatyzacja i dedykowane wsparcie",
        price: 5999,
        tokens: 18000,
        users: 10,
        features: [
          { text: "Wszystkie funkcje z Planu Pro", featureKey: "all_from_pro", included: true },
          { text: "Analityk AI (apply actions)", featureKey: "ai_analyst_actions", included: true },
          { text: "Pełny tracking + tag rules", featureKey: "full_tracking_tags", included: true },
          { text: "CAPI (Meta/Google/TikTok)", featureKey: "capi", included: true },
          { text: "Panel agencyjny", featureKey: "agency_panel", included: true },
          { text: "External API + webhooks", featureKey: "api_webhooks", included: true },
          { text: "Dedykowany opiekun konta", featureKey: "dedicated_manager", included: true },
          { text: "Priorytetowe wsparcie z SLA", featureKey: "priority_sla", included: true },
        ],
      },
    ],
  },
  {
    id: "ecommerce",
    name: "E-commerce",
    description: "Tracking, analityka i automatyzacja dla sklepów internetowych",
    icon: <ShoppingCart className="w-5 h-5" />,
    comingSoon: true,
    tiers: [
      {
        tier: "start",
        name: "Basic",
        subtitle: "Analityka i automatyzacja podstawowych działań",
        price: 499,
        tokens: 2000,
        users: 1,
        features: [
          { text: "Analityk AI (readonly)", featureKey: "ai_analyst_readonly", included: true },
          { text: "Google Merchant Center", featureKey: "google_merchant", included: true },
          { text: "Tracking podstawowy (pixel)", featureKey: "tracking_pixel", included: true },
          { text: "Wsparcie email", featureKey: "email_support", included: true },
          { text: "Allegro + Allegro Ads", featureKey: "allegro", included: false },
          { text: "Shopify / BaseLinker", featureKey: "shopify_baselinker", included: false },
          { text: "Twórca reklam", featureKey: "ad_creator", included: false },
          { text: "Panel agencyjny", featureKey: "agency_panel", included: false },
        ],
      },
      {
        tier: "pro",
        name: "Business",
        subtitle: "Inteligentne rekomendacje i pełna analityka",
        price: 1999,
        bestSeller: true,
        tokens: 6000,
        users: 3,
        features: [
          { text: "Wszystkie funkcje z Planu Basic", featureKey: "all_from_basic", included: true },
          {
            text: "Analityk AI (rekomendacje)",
            featureKey: "ai_analyst_recommendations",
            included: true,
          },
          { text: "Allegro + Allegro Ads", featureKey: "allegro", included: true },
          { text: "Shopify / BaseLinker", featureKey: "shopify_baselinker", included: true },
          {
            text: "Pełny tracking + atrybucja",
            featureKey: "full_tracking_attribution",
            included: true,
          },
          { text: "Twórca reklam", featureKey: "ad_creator", included: true },
          { text: "Priorytetowe wsparcie", featureKey: "priority_support", included: true },
          { text: "Panel agencyjny", featureKey: "agency_panel", included: false },
        ],
      },
      {
        tier: "expert",
        name: "Expert",
        subtitle: "Pełna automatyzacja i dedykowane wsparcie",
        price: 6999,
        tokens: 18000,
        users: 10,
        features: [
          {
            text: "Wszystkie funkcje z Planu Business",
            featureKey: "all_from_business",
            included: true,
          },
          { text: "Automatyczne kampanie Meta", featureKey: "auto_meta_campaigns", included: true },
          { text: "Campaign Creator", featureKey: "campaign_creator_feature", included: true },
          { text: "Tag rules + webhooks", featureKey: "tag_rules_webhooks", included: true },
          { text: "Panel agencyjny", featureKey: "agency_panel", included: true },
          { text: "External API", featureKey: "external_api", included: true },
          { text: "Dedykowany opiekun konta", featureKey: "dedicated_manager", included: true },
          { text: "Priorytetowe wsparcie z SLA", featureKey: "priority_sla", included: true },
        ],
      },
    ],
  },
  {
    id: "content_creation",
    name: "Organic",
    description: "Zakładka Organic: Inspiracje, reelsy, grafiki — dla twórców i marek osobistych",
    icon: <Film className="w-5 h-5" />,
    tiers: [
      {
        tier: "start",
        name: "Organic",
        subtitle: "Cała zakładka Organic + daily viral Inspiracje",
        price: 99,
        bestSeller: true,
        tokens: 900,
        users: 1,
        features: [
          { text: "Feed Inspiracji (daily viral)", featureKey: "inspiration_feed", included: true },
          { text: "Generator skryptów i hooków", featureKey: "script_generator", included: true },
          { text: "Twórca reelsów", featureKey: "reel_creator", included: true },
          { text: "Generacja grafik AI", featureKey: "graphics_ai", included: true },
          { text: "Scheduler publikacji", featureKey: "scheduler", included: true },
          {
            text: "Asystent wiadomości + Analityka",
            featureKey: "assistant_analytics",
            included: true,
          },
        ],
      },
    ],
  },
];

// ═══════════════════════════════════════════════════════
// Tier badge icon map
// ═══════════════════════════════════════════════════════

const TIER_ICONS: Record<Tier, React.ReactNode> = {
  start: <Star className="w-4 h-4" />,
  pro: <Sparkles className="w-4 h-4" />,
  expert: <Crown className="w-4 h-4" />,
};

const TIER_COLORS: Record<Tier, string> = {
  start: "border-white/[0.08] bg-white/[0.03]",
  pro: "border-[#ef3e29]/40 bg-[#ef3e29]/[0.05]",
  expert: "border-amber-500/30 bg-amber-500/[0.05]",
};

const TIER_ACCENT: Record<Tier, string> = {
  start: "text-white/70",
  pro: "text-[#ef3e29]",
  expert: "text-amber-400",
};

// ═══════════════════════════════════════════════════════
// Cart types
// ═══════════════════════════════════════════════════════

interface CartItem {
  vertical: Vertical;
  verticalName: string;
  tier: Tier;
  tierName: string;
  price: number;
  tokens: number;
}

// ═══════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════

type BillingCycle = "monthly" | "yearly";

interface PricingCardProps {
  plan: PricingTier;
  isCurrentPlan: boolean;
  isSelected: boolean;
  verticalId: Vertical;
  billingCycle: BillingCycle;
  onToggleSelect: (
    vertical: Vertical,
    tier: Tier,
    tierName: string,
    price: number,
    tokens: number
  ) => void;
}

function PricingCard({
  plan,
  isCurrentPlan,
  isSelected,
  verticalId,
  billingCycle,
  onToggleSelect,
}: PricingCardProps) {
  const { t } = useTranslation("billingPanel");
  const displayPrice = billingCycle === "yearly" ? Math.round(plan.price * 0.8) : plan.price;

  function handleSelect() {
    onToggleSelect(verticalId, plan.tier, plan.name, displayPrice, plan.tokens);
  }

  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, type: "spring", stiffness: 120, damping: 20 }}
      className={`relative rounded-xl border p-6 transition-all hover:border-white/20 ${
        TIER_COLORS[plan.tier]
      } ${plan.bestSeller ? "ring-1 ring-[#ef3e29]/30" : ""} ${
        isSelected ? "ring-2 ring-emerald-400/60 border-emerald-400/40" : ""
      }`}
    >
      {/* Bestseller badge */}
      {plan.bestSeller && !isSelected && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-[#ef3e29] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
            {t("pricingCard.bestseller")}
          </span>
        </div>
      )}

      {/* Selected badge */}
      {isSelected && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1">
            <Check className="w-3 h-3" /> {t("pricingCard.inCart")}
          </span>
        </div>
      )}

      {/* Current plan badge */}
      {isCurrentPlan && (
        <div className="absolute -top-3 right-4">
          <span className="bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
            {t("pricingCard.yourPlan")}
          </span>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <span className={TIER_ACCENT[plan.tier]}>{TIER_ICONS[plan.tier]}</span>
          <h3 className="text-white font-semibold text-lg">{plan.name}</h3>
        </div>
        <p className="text-white/50 text-xs">
          {t(`verticals.${verticalId}.${plan.tier}.subtitle`)}
        </p>
      </div>

      {/* Price */}
      <div className="mb-6">
        <div className="flex items-baseline gap-1">
          <span className="text-white text-heading-1 font-extrabold tabular-nums">
            <NumberFlow
              value={displayPrice}
              format={{ style: "decimal", minimumFractionDigits: 0 }}
            />
          </span>
          <span className="text-white/40 text-sm">{t("pricingCard.priceSuffix")}</span>
        </div>
        <p className="text-white/30 text-xs mt-0.5">{t("pricingCard.vatNote")}</p>
        {billingCycle === "yearly" && (
          <p className="text-emerald-400 text-xs mt-0.5">
            {t("pricingCard.yearlySavings", {
              yearlyTotal: (displayPrice * 12).toLocaleString("pl-PL"),
              savings: ((plan.price - displayPrice) * 12).toLocaleString("pl-PL"),
            })}
          </p>
        )}
        <p className="text-white/30 text-xs mt-1">
          {plan.tokens.toLocaleString("pl-PL")} {t("pricingCard.tokensLabel")} · {plan.users}{" "}
          {plan.users === 1 ? t("pricingCard.userSingular") : t("pricingCard.userPlural")}
        </p>
      </div>

      {/* Features */}
      <ul className="space-y-2.5 mb-6">
        {plan.features.map((feature) => (
          <li key={feature.featureKey} className="flex items-start gap-2.5">
            {feature.included ? (
              <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
            ) : (
              <span className="w-4 h-4 rounded-full border border-white/10 mt-0.5 flex-shrink-0" />
            )}
            <span className={`text-sm ${feature.included ? "text-white/80" : "text-white/30"}`}>
              {t(`features.${feature.featureKey}`, { defaultValue: feature.text })}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      {isCurrentPlan ? (
        <button
          disabled
          className="w-full py-2.5 rounded-lg bg-emerald-500/20 text-emerald-400 text-sm font-medium cursor-default"
        >
          {t("pricingCard.activePlan")}
        </button>
      ) : (
        <button
          className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            isSelected
              ? "bg-emerald-500/20 text-emerald-400 hover:bg-red-500/20 hover:text-red-400"
              : plan.bestSeller
                ? "bg-[#ef3e29] hover:bg-[#d63520] text-white"
                : "bg-white/[0.08] hover:bg-white/[0.12] text-white"
          }`}
          onClick={handleSelect}
        >
          {isSelected ? t("pricingCard.removeFromCart") : t("pricingCard.addToCart")}
        </button>
      )}
    </motion.div>
  );
}

function VerticalSection({
  vertical,
  billingCycle,
  selectedTier,
  onToggleSelect,
}: {
  vertical: VerticalConfig;
  billingCycle: BillingCycle;
  selectedTier: Tier | null;
  onToggleSelect: (
    vertical: Vertical,
    tier: Tier,
    tierName: string,
    price: number,
    tokens: number
  ) => void;
}) {
  const { user } = useAuth();
  const { t } = useTranslation("billingPanel");
  const userPlanKey = (user as any)?.subscriptionPlanKey as string | undefined;
  // Support multi-plan keys (comma-separated)
  const activePlanKeys = userPlanKey?.split(",") ?? [];
  const currentVerticalTier =
    activePlanKeys.find((key) => key.startsWith(vertical.id + ":"))?.split(":")[1] ?? null;

  return (
    <div className="mb-10">
      {/* Vertical header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-8 h-8 rounded-lg bg-white/[0.06] flex items-center justify-center text-white/60">
          {vertical.icon}
        </div>
        <div>
          <h2 className="text-white font-semibold text-base">{vertical.name}</h2>
          <p className="text-white/40 text-xs">{t(`verticals.${vertical.id}.description`)}</p>
        </div>
        {vertical.comingSoon ? (
          <span className="ml-auto text-[10px] uppercase tracking-wider text-amber-400/70 border border-amber-400/20 bg-amber-400/[0.06] px-2.5 py-0.5 rounded-full">
            {t("vertical.comingSoon")}
          </span>
        ) : (
          <span className="ml-auto text-[10px] uppercase tracking-wider text-white/20 border border-white/[0.08] px-2 py-0.5 rounded-full">
            {t("vertical.optional")}
          </span>
        )}
      </div>

      {/* Tier cards */}
      <div
        className={`relative grid gap-4 ${vertical.tiers.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3"}`}
      >
        {vertical.comingSoon && (
          <div className="absolute inset-0 z-10 bg-black/50 backdrop-blur-[2px] rounded-xl flex items-center justify-center">
            <p className="text-amber-400/80 text-sm font-medium">
              {t("vertical.comingSoonOverlay")}
            </p>
          </div>
        )}
        {vertical.tiers.map((tier) => (
          <PricingCard
            key={tier.tier}
            plan={tier}
            isCurrentPlan={tier.tier === currentVerticalTier}
            isSelected={!vertical.comingSoon && tier.tier === selectedTier}
            verticalId={vertical.id}
            billingCycle={billingCycle}
            onToggleSelect={vertical.comingSoon ? () => {} : onToggleSelect}
          />
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Vertical name lookup
// ═══════════════════════════════════════════════════════

const VERTICAL_NAMES: Record<Vertical, string> = {
  lead_generation: "Lead Generation",
  ecommerce: "E-commerce",
  content_creation: "Content Creation",
};

// ═══════════════════════════════════════════════════════
// Floating Cart Bar
// ═══════════════════════════════════════════════════════

interface PackItem {
  priceId: string;
  accounts: number;
  price: number;
}

interface CartBarProps {
  cart: Map<Vertical, CartItem>;
  packItem: PackItem | null;
  billingCycle: BillingCycle;
  onRemove: (vertical: Vertical) => void;
  onRemovePack: () => void;
  onCheckout: () => void;
  isLoading: boolean;
  checkoutError: string | null;
}

function CartBar({
  cart,
  packItem,
  billingCycle,
  onRemove,
  onRemovePack,
  onCheckout,
  isLoading,
  checkoutError,
}: CartBarProps) {
  const { t } = useTranslation("billingPanel");
  const isEmpty = cart.size === 0 && !packItem;
  if (isEmpty) return null;

  const planItems = Array.from(cart.values());
  const totalPrice = planItems.reduce((sum, item) => sum + item.price, 0) + (packItem?.price ?? 0);
  const totalTokens = planItems.reduce((sum, item) => sum + item.tokens, 0);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-xl border-t border-white/[0.08] shadow-2xl">
      <div className="max-w-5xl mx-auto px-4 py-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Cart items */}
          <div className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto">
            <ShoppingCart className="w-4 h-4 text-white/40 flex-shrink-0" />
            {planItems.map((item) => (
              <div
                key={item.vertical}
                className="flex items-center gap-1.5 bg-white/[0.06] rounded-lg px-3 py-1.5 flex-shrink-0"
              >
                <span className="text-white text-xs font-medium">{item.verticalName}</span>
                <span className="text-white/40 text-xs">·</span>
                <span className="text-emerald-400 text-xs">{item.tierName}</span>
                <span className="text-white/30 text-xs">
                  {item.price} {t("cart.priceSuffix")}
                </span>
                <button
                  onClick={() => onRemove(item.vertical)}
                  className="ml-1 text-white/30 hover:text-red-400 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {packItem && (
              <div className="flex items-center gap-1.5 bg-white/[0.06] rounded-lg px-3 py-1.5 flex-shrink-0">
                <span className="text-white text-xs font-medium">
                  {t("cart.packAccounts", { count: packItem.accounts })}
                </span>
                <span className="text-white/40 text-xs">·</span>
                <span className="text-emerald-400 text-xs">{t("cart.packCategory")}</span>
                <span className="text-white/30 text-xs">
                  {packItem.price} {t("cart.priceSuffix")}
                </span>
                <button
                  onClick={onRemovePack}
                  className="ml-1 text-white/30 hover:text-red-400 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          {/* Summary + checkout */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="text-right">
              <p className="text-white font-bold text-sm">
                {totalPrice.toLocaleString("pl-PL")}{" "}
                <span className="text-white/40 font-normal text-xs">{t("cart.totalSuffix")}</span>
              </p>
              <p className="text-white/30 text-[10px]">
                {totalTokens > 0
                  ? `${totalTokens.toLocaleString("pl-PL")} ${t("pricingCard.tokensLabel")} · `
                  : ""}
                {billingCycle === "yearly" ? t("cart.billingYearly") : t("cart.billingMonthly")}
              </p>
            </div>
            <button
              disabled={isLoading}
              onClick={onCheckout}
              className={`bg-[#ef3e29] hover:bg-[#d63520] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                isLoading ? "opacity-60 cursor-wait" : ""
              }`}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4" />
              )}
              {t("cart.checkout")}
            </button>
          </div>
        </div>
        {checkoutError && <p className="text-red-400 text-xs mt-2 text-center">{checkoutError}</p>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Ad account packs
// ═══════════════════════════════════════════════════════

const AD_ACCOUNT_PACKS = [
  { priceId: "price_1THMstRxO0w5bTlQWxUhCGY6", accounts: 5, price: 79 },
  { priceId: "price_1TJFqNRxO0w5bTlQGWmG53tc", accounts: 15, price: 179 },
  { priceId: "price_1TJFquRxO0w5bTlQ5bJMv4yx", accounts: 50, price: 399 },
  { priceId: "price_1TJFrDRxO0w5bTlQTnHHRi2a", accounts: 250, price: 999 },
];

interface AdAccountPacksSectionProps {
  selectedPack: PackItem | null;
  onSelectPack: (pack: PackItem | null) => void;
}

function AdAccountPacksSection({ selectedPack, onSelectPack }: AdAccountPacksSectionProps) {
  const { user } = useAuth();
  const { t } = useTranslation("billingPanel");
  const currentPack = (user as any)?.adAccountPackSize as number | undefined;

  function handleTogglePack(pack: (typeof AD_ACCOUNT_PACKS)[number]) {
    if (selectedPack?.priceId === pack.priceId) {
      onSelectPack(null);
      return;
    }
    onSelectPack({ priceId: pack.priceId, accounts: pack.accounts, price: pack.price });
  }

  return (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-white/[0.06] flex items-center justify-center text-white/60">
          <MonitorCheck className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-white font-semibold text-base">{t("adPacks.title")}</h2>
          <p className="text-white/40 text-xs">{t("adPacks.description")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {AD_ACCOUNT_PACKS.map((pack) => {
          const isActive = currentPack === pack.accounts;
          const isSelectedPack = selectedPack?.priceId === pack.priceId;
          return (
            <div
              key={pack.priceId}
              className={`relative rounded-xl border p-5 transition-all ${
                isActive
                  ? "border-emerald-400/40 bg-emerald-500/[0.05] ring-1 ring-emerald-400/30"
                  : isSelectedPack
                    ? "border-emerald-400/40 bg-emerald-500/[0.05] ring-2 ring-emerald-400/60"
                    : "border-white/[0.08] bg-white/[0.03] hover:border-white/20"
              }`}
            >
              {isActive && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1">
                    <Check className="w-3 h-3" /> {t("adPacks.activeBadge")}
                  </span>
                </div>
              )}
              <p className="text-white/40 text-xs mb-1">{t("adPacks.packLabel")}</p>
              <p className="text-white text-heading-1 font-extrabold mb-4">+{pack.accounts}</p>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-white font-semibold">
                  {pack.price} {t("cart.priceSuffix")}
                </span>
                <span className="text-white/30 text-xs">{t("adPacks.priceSuffix")}</span>
              </div>
              <button
                disabled={isActive}
                onClick={() => handleTogglePack(pack)}
                className={`w-full py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  isActive
                    ? "bg-emerald-500/20 text-emerald-400 cursor-default"
                    : isSelectedPack
                      ? "bg-emerald-500/20 text-emerald-400 hover:bg-red-500/20 hover:text-red-400"
                      : "bg-white/[0.08] hover:bg-white/[0.14] text-white"
                }`}
              >
                {isActive
                  ? t("adPacks.activePack")
                  : isSelectedPack
                    ? t("adPacks.removeFromCart")
                    : t("adPacks.addToCart")}
              </button>
            </div>
          );
        })}
      </div>

      {currentPack && currentPack > 0 && (
        <p className="text-white/30 text-xs mt-3">
          {t("adPacks.activeInfo", { count: currentPack })}
        </p>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Animated Billing Cycle Toggle
// ═══════════════════════════════════════════════════════

function BillingToggle({
  billingCycle,
  onChange,
}: {
  billingCycle: BillingCycle;
  onChange: (cycle: BillingCycle) => void;
}) {
  const monthlyRef = useRef<HTMLButtonElement>(null);
  const yearlyRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation("billingPanel");
  const [pillStyle, setPillStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    const ref = billingCycle === "monthly" ? monthlyRef : yearlyRef;
    if (ref.current) {
      setPillStyle({
        width: ref.current.offsetWidth,
        transform: `translateX(${ref.current.offsetLeft}px)`,
      });
    }
  }, [billingCycle]);

  function handleSwitch(cycle: BillingCycle) {
    if (billingCycle === cycle) return;
    onChange(cycle);
    if (cycle === "yearly" && yearlyRef.current) {
      const rect = yearlyRef.current.getBoundingClientRect();
      confetti({
        particleCount: 70,
        spread: 70,
        origin: {
          x: (rect.left + rect.width / 2) / window.innerWidth,
          y: (rect.top + rect.height / 2) / window.innerHeight,
        },
        colors: ["#ef3e29", "#ff6b55", "#ffffff", "#ffd4ce"],
        ticks: 250,
        gravity: 1.2,
        decay: 0.94,
        startVelocity: 28,
      });
    }
  }

  return (
    <div className="flex justify-center">
      <div className="relative flex w-fit items-center rounded-full bg-white/[0.06] border border-white/[0.08] p-1">
        <motion.div
          className="absolute left-0 top-0 h-full rounded-full bg-[#ef3e29] p-1"
          style={pillStyle}
          transition={{ type: "spring", stiffness: 500, damping: 40 }}
        />
        <button
          ref={monthlyRef}
          onClick={() => handleSwitch("monthly")}
          className={`relative z-10 rounded-full px-5 py-2 text-sm font-medium transition-colors ${
            billingCycle === "monthly" ? "text-white" : "text-white/40 hover:text-white/70"
          }`}
        >
          {t("billing.monthly")}
        </button>
        <button
          ref={yearlyRef}
          onClick={() => handleSwitch("yearly")}
          className={`relative z-10 rounded-full px-5 py-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
            billingCycle === "yearly" ? "text-white" : "text-white/40 hover:text-white/70"
          }`}
        >
          {t("billing.yearly")}
          <span
            className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
              billingCycle === "yearly"
                ? "bg-white/20 text-white"
                : "bg-emerald-500/20 text-emerald-400"
            }`}
          >
            -20%
          </span>
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Cancel subscription flow
// ═══════════════════════════════════════════════════════

type CancelStep = "survey" | "lose" | "save" | "confirm" | "done";

const CANCEL_REASONS: { id: string }[] = [
  { id: "price" },
  { id: "usage" },
  { id: "features" },
  { id: "competitor" },
  { id: "technical" },
  { id: "other" },
];

// Highest-priority reason → which save offer to show. First match wins.
const REASON_PRIORITY = ["competitor", "price", "features", "usage", "technical", "other"] as const;
type SaveStrategy = "discount" | "pause" | "feedback" | "support" | "generic";

const SUPPORT_CALL_URL =
  "https://api.whitepalmgroup.com/widget/bookings/viralpartners-support-call";

function pickStrategy(reasons: Set<string>): { reason: string; strategy: SaveStrategy } {
  for (const r of REASON_PRIORITY) {
    if (reasons.has(r)) {
      switch (r) {
        case "competitor":
          return { reason: r, strategy: "discount" };
        case "price":
          return { reason: r, strategy: "discount" };
        case "features":
          return { reason: r, strategy: "feedback" };
        case "usage":
          return { reason: r, strategy: "pause" };
        case "technical":
          return { reason: r, strategy: "support" };
        case "other":
          return { reason: r, strategy: "generic" };
      }
    }
  }
  return { reason: "other", strategy: "generic" };
}

interface UsageSummary {
  monthlyTokenAllocation: number;
  tokensUsedThisMonth: number;
  publishedPosts: number;
  scheduledPosts: number;
  totalPosts: number;
  memberSinceIso: string | null;
  planKey: string | null;
}

type CancelOutcome = "kept" | "accepted_discount" | "accepted_pause" | "cancelled";

function CancelFlowModal({
  onClose,
  onCancelled,
}: {
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [step, setStep] = useState<CancelStep>("survey");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [freeText, setFreeText] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation("billingPanel");

  const { reason: primaryReason, strategy } = pickStrategy(selected);

  function toggleReason(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function persistFeedback(outcome: CancelOutcome) {
    try {
      await apiRequest("POST", "/api/billing/cancellation-feedback", {
        reasons: Array.from(selected),
        freeText: freeText.trim() || undefined,
        outcome,
      });
    } catch {
      // Telemetry is best-effort — never block the user flow.
    }
  }

  async function handleSurveyNext() {
    if (selected.size === 0) return;
    setUsageLoading(true);
    setStep("lose");
    try {
      const data = (await apiRequest("GET", "/api/billing/usage-summary")) as UsageSummary;
      setUsage(data);
    } catch {
      setUsage(null);
    } finally {
      setUsageLoading(false);
    }
  }

  async function handleConfirmCancel() {
    setIsBusy(true);
    try {
      await apiRequest("POST", "/api/billing/cancel");
      await persistFeedback("cancelled");
      setStep("done");
      onCancelled();
    } catch {
      toast({
        title: t("cancelFlow.toastRetryTitle"),
        description: t("cancelFlow.toastRetryDesc"),
        variant: "destructive",
      });
    } finally {
      setIsBusy(false);
    }
  }

  async function handleAcceptDiscount() {
    setIsBusy(true);
    try {
      const tier =
        primaryReason === "competitor"
          ? "competitor"
          : primaryReason === "price"
            ? "price"
            : "standard";
      await apiRequest("POST", "/api/billing/retention-discount", { tier });
      await persistFeedback("accepted_discount");
      toast({
        title: t("cancelFlow.toastDiscountTitle"),
        description: t("cancelFlow.toastDiscountDesc"),
      });
      onClose();
    } catch {
      toast({
        title: t("cancelFlow.toastRetryTitle"),
        description: t("cancelFlow.toastRetryDesc"),
        variant: "destructive",
      });
    } finally {
      setIsBusy(false);
    }
  }

  async function handleAcceptPause() {
    setIsBusy(true);
    try {
      await apiRequest("POST", "/api/billing/pause", { days: 30 });
      await persistFeedback("accepted_pause");
      toast({
        title: t("cancelFlow.toastPauseTitle"),
        description: t("cancelFlow.toastPauseDesc"),
      });
      onClose();
    } catch {
      toast({
        title: t("cancelFlow.toastRetryTitle"),
        description: t("cancelFlow.toastRetryDesc"),
        variant: "destructive",
      });
    } finally {
      setIsBusy(false);
    }
  }

  async function handleSendFeedbackAndStay() {
    setIsBusy(true);
    try {
      await persistFeedback("kept");
      toast({
        title: t("cancelFlow.toastFeedbackTitle"),
        description: t("cancelFlow.toastFeedbackDesc"),
      });
      onClose();
    } finally {
      setIsBusy(false);
    }
  }

  function handleCloseAsKept() {
    // Modal dismissed without cancelling — record as "kept" if the user picked any reasons.
    if (selected.size > 0) void persistFeedback("kept");
    onClose();
  }

  // Discount tier amounts (must mirror server RETENTION_TIERS)
  const discountPercent = primaryReason === "competitor" ? 40 : primaryReason === "price" ? 30 : 20;
  const discountMonths = primaryReason === "competitor" || primaryReason === "price" ? 3 : 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-[#0f1117] border border-white/[0.08] rounded-2xl shadow-2xl overflow-hidden">
        {/* Step: Survey */}
        {step === "survey" && (
          <div className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <h3 className="text-white font-semibold text-base">{t("cancelFlow.surveyTitle")}</h3>
            </div>
            <p className="text-white/40 text-sm mb-5">{t("cancelFlow.surveyDesc")}</p>
            <div className="space-y-2 mb-4">
              {CANCEL_REASONS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => toggleReason(r.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium text-left transition-all ${
                    selected.has(r.id)
                      ? "border-[#ef3e29]/50 bg-[#ef3e29]/[0.08] text-white"
                      : "border-white/[0.07] bg-white/[0.03] text-white/60 hover:border-white/20 hover:text-white/80"
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${selected.has(r.id) ? "bg-[#ef3e29] border-[#ef3e29]" : "border-white/20"}`}
                  >
                    {selected.has(r.id) && <Check className="w-3 h-3 text-white" />}
                  </span>
                  {t(`cancelReasons.${r.id}`)}
                </button>
              ))}
            </div>
            <textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value.slice(0, 2000))}
              placeholder={t("cancelFlow.freeTextPlaceholder")}
              rows={3}
              className="w-full px-4 py-3 mb-5 rounded-xl bg-white/[0.03] border border-white/[0.07] text-sm text-white placeholder:text-white/30 focus:border-white/20 focus:outline-none resize-none"
            />
            <div className="flex gap-3">
              <button
                onClick={handleCloseAsKept}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white/50 hover:text-white/70 border border-white/[0.07] transition-colors"
              >
                {t("cancelFlow.stay")}
              </button>
              <button
                onClick={handleSurveyNext}
                disabled={selected.size === 0}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-white/[0.08] text-white hover:bg-white/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {t("cancelFlow.next")}
              </button>
            </div>
          </div>
        )}

        {/* Step: What you'll lose */}
        {step === "lose" && (
          <div className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="w-4 h-4 text-amber-400" />
              <h3 className="text-white font-semibold text-base">{t("cancelFlow.loseTitle")}</h3>
            </div>
            <p className="text-white/40 text-sm mb-5">{t("cancelFlow.loseDesc")}</p>
            {usageLoading && (
              <div className="flex items-center justify-center py-8 text-white/40">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            )}
            {!usageLoading && usage && (
              <div className="grid grid-cols-2 gap-2 mb-5">
                <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3">
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">
                    {t("cancelFlow.statPosts")}
                  </p>
                  <p className="text-white font-semibold text-lg">{usage.publishedPosts}</p>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3">
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">
                    {t("cancelFlow.statScheduled")}
                  </p>
                  <p className="text-white font-semibold text-lg">{usage.scheduledPosts}</p>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3">
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">
                    {t("cancelFlow.statTokens")}
                  </p>
                  <p className="text-white font-semibold text-lg">
                    {usage.tokensUsedThisMonth.toLocaleString()}
                  </p>
                  <p className="text-white/30 text-[10px] mt-0.5">
                    / {usage.monthlyTokenAllocation.toLocaleString()}{" "}
                    {t("cancelFlow.statTokensSuffix")}
                  </p>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.07] rounded-xl p-3">
                  <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">
                    {t("cancelFlow.statMember")}
                  </p>
                  <p className="text-white font-semibold text-lg">
                    {usage.memberSinceIso
                      ? new Date(usage.memberSinceIso).toLocaleDateString()
                      : "—"}
                  </p>
                </div>
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleCloseAsKept}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white/50 hover:text-white/70 border border-white/[0.07] transition-colors"
              >
                {t("cancelFlow.stay")}
              </button>
              <button
                onClick={() => setStep("save")}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-white/[0.08] text-white hover:bg-white/[0.12] transition-all"
              >
                {t("cancelFlow.continue")}
              </button>
            </div>
          </div>
        )}

        {/* Step: Reason-specific save offer */}
        {step === "save" && (
          <div className="p-6">
            {strategy === "discount" && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-white font-semibold text-base">
                    {t("cancelFlow.offerTitle")}
                  </h3>
                </div>
                <p className="text-white/40 text-sm mb-5">
                  {primaryReason === "competitor"
                    ? t("cancelFlow.offerCompetitorDesc")
                    : t("cancelFlow.offerPriceDesc")}
                </p>
                <div className="bg-gradient-to-br from-emerald-500/[0.08] to-emerald-500/[0.04] border border-emerald-500/20 rounded-xl p-5 mb-5">
                  <p className="text-emerald-400 font-extrabold text-heading-1 mb-1">
                    -{discountPercent}%
                  </p>
                  <p className="text-white font-semibold text-sm mb-1">
                    {t("cancelFlow.offerForMonths", { count: discountMonths })}
                  </p>
                  <p className="text-white/40 text-xs">{t("cancelFlow.offerFinePrint")}</p>
                </div>
                <button
                  onClick={handleAcceptDiscount}
                  disabled={isBusy}
                  className="w-full py-3 mb-3 rounded-xl text-sm font-semibold bg-emerald-500 hover:bg-emerald-600 text-white transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Gift className="w-4 h-4" />
                  )}
                  {t("cancelFlow.acceptDiscount")}
                </button>
                <button
                  onClick={() => setStep("confirm")}
                  className="w-full py-2 text-sm text-white/30 hover:text-white/50 transition-colors"
                >
                  {t("cancelFlow.rejectOffer")}
                </button>
              </>
            )}

            {strategy === "pause" && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Pause className="w-4 h-4 text-blue-400" />
                  <h3 className="text-white font-semibold text-base">
                    {t("cancelFlow.pauseTitle")}
                  </h3>
                </div>
                <p className="text-white/40 text-sm mb-5">{t("cancelFlow.pauseDesc")}</p>
                <div className="bg-gradient-to-br from-blue-500/[0.08] to-blue-500/[0.04] border border-blue-500/20 rounded-xl p-5 mb-5">
                  <p className="text-blue-400 font-extrabold text-heading-1 mb-1">
                    30 {t("cancelFlow.daysShort")}
                  </p>
                  <p className="text-white font-semibold text-sm mb-1">
                    {t("cancelFlow.pauseSubtitle")}
                  </p>
                  <p className="text-white/40 text-xs">{t("cancelFlow.pauseFinePrint")}</p>
                </div>
                <button
                  onClick={handleAcceptPause}
                  disabled={isBusy}
                  className="w-full py-3 mb-3 rounded-xl text-sm font-semibold bg-blue-500 hover:bg-blue-600 text-white transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Pause className="w-4 h-4" />
                  )}
                  {t("cancelFlow.acceptPause")}
                </button>
                <a
                  href={SUPPORT_CALL_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-2 text-xs text-white/40 hover:text-white/60 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Calendar className="w-3 h-3" />
                  {t("cancelFlow.bookOnboarding")}
                </a>
                <button
                  onClick={() => setStep("confirm")}
                  className="w-full py-2 text-sm text-white/30 hover:text-white/50 transition-colors"
                >
                  {t("cancelFlow.rejectOffer")}
                </button>
              </>
            )}

            {strategy === "feedback" && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare className="w-4 h-4 text-purple-400" />
                  <h3 className="text-white font-semibold text-base">
                    {t("cancelFlow.featureTitle")}
                  </h3>
                </div>
                <p className="text-white/40 text-sm mb-5">{t("cancelFlow.featureDesc")}</p>
                <textarea
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value.slice(0, 2000))}
                  placeholder={t("cancelFlow.featurePlaceholder")}
                  rows={4}
                  className="w-full px-4 py-3 mb-5 rounded-xl bg-white/[0.03] border border-white/[0.07] text-sm text-white placeholder:text-white/30 focus:border-white/20 focus:outline-none resize-none"
                />
                <button
                  onClick={handleSendFeedbackAndStay}
                  disabled={isBusy || freeText.trim().length === 0}
                  className="w-full py-3 mb-3 rounded-xl text-sm font-semibold bg-purple-500 hover:bg-purple-600 text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {isBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <MessageSquare className="w-4 h-4" />
                  )}
                  {t("cancelFlow.sendAndStay")}
                </button>
                <button
                  onClick={() => setStep("confirm")}
                  className="w-full py-2 text-sm text-white/30 hover:text-white/50 transition-colors"
                >
                  {t("cancelFlow.rejectOffer")}
                </button>
              </>
            )}

            {strategy === "support" && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Headphones className="w-4 h-4 text-cyan-400" />
                  <h3 className="text-white font-semibold text-base">
                    {t("cancelFlow.supportTitle")}
                  </h3>
                </div>
                <p className="text-white/40 text-sm mb-5">{t("cancelFlow.supportDesc")}</p>
                <div className="bg-gradient-to-br from-cyan-500/[0.08] to-cyan-500/[0.04] border border-cyan-500/20 rounded-xl p-5 mb-5">
                  <p className="text-white font-semibold text-sm mb-1">
                    {t("cancelFlow.supportSubtitle")}
                  </p>
                  <p className="text-white/40 text-xs">{t("cancelFlow.supportFinePrint")}</p>
                </div>
                <a
                  href={SUPPORT_CALL_URL}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => {
                    void persistFeedback("kept");
                  }}
                  className="w-full py-3 mb-3 rounded-xl text-sm font-semibold bg-cyan-500 hover:bg-cyan-600 text-white transition-all flex items-center justify-center gap-2"
                >
                  <Calendar className="w-4 h-4" />
                  {t("cancelFlow.bookSupportCall")}
                </a>
                <button
                  onClick={() => setStep("confirm")}
                  className="w-full py-2 text-sm text-white/30 hover:text-white/50 transition-colors"
                >
                  {t("cancelFlow.rejectOffer")}
                </button>
              </>
            )}

            {strategy === "generic" && (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Pause className="w-4 h-4 text-blue-400" />
                  <h3 className="text-white font-semibold text-base">
                    {t("cancelFlow.pauseTitle")}
                  </h3>
                </div>
                <p className="text-white/40 text-sm mb-5">{t("cancelFlow.pauseDesc")}</p>
                <div className="bg-gradient-to-br from-blue-500/[0.08] to-blue-500/[0.04] border border-blue-500/20 rounded-xl p-5 mb-5">
                  <p className="text-blue-400 font-extrabold text-heading-1 mb-1">
                    30 {t("cancelFlow.daysShort")}
                  </p>
                  <p className="text-white font-semibold text-sm mb-1">
                    {t("cancelFlow.pauseSubtitle")}
                  </p>
                  <p className="text-white/40 text-xs">{t("cancelFlow.pauseFinePrint")}</p>
                </div>
                <button
                  onClick={handleAcceptPause}
                  disabled={isBusy}
                  className="w-full py-3 mb-3 rounded-xl text-sm font-semibold bg-blue-500 hover:bg-blue-600 text-white transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {isBusy ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Pause className="w-4 h-4" />
                  )}
                  {t("cancelFlow.acceptPause")}
                </button>
                <button
                  onClick={() => setStep("confirm")}
                  className="w-full py-2 text-sm text-white/30 hover:text-white/50 transition-colors"
                >
                  {t("cancelFlow.rejectOffer")}
                </button>
              </>
            )}
          </div>
        )}

        {/* Step: Final confirm */}
        {step === "confirm" && (
          <div className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <h3 className="text-white font-semibold text-base">{t("cancelFlow.confirmTitle")}</h3>
            </div>
            <p className="text-white/40 text-sm mb-5">{t("cancelFlow.confirmDesc")}</p>
            <div className="bg-red-500/[0.06] border border-red-500/20 rounded-xl p-4 mb-4 text-sm text-red-300/70">
              {t("cancelFlow.confirmWarning")}
            </div>
            {strategy !== "pause" && strategy !== "generic" && (
              <button
                onClick={handleAcceptPause}
                disabled={isBusy}
                className="w-full py-2.5 mb-3 rounded-xl text-xs font-medium bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 border border-blue-500/20 transition-all flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                <Pause className="w-3 h-3" />
                {t("cancelFlow.pauseInstead")}
              </button>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleCloseAsKept}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-white/[0.08] text-white hover:bg-white/[0.12] transition-all"
              >
                {t("cancelFlow.stay")}
              </button>
              <button
                onClick={handleConfirmCancel}
                disabled={isBusy}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-500/20 text-red-400 hover:bg-red-500/30 disabled:opacity-60 transition-all flex items-center justify-center gap-2"
              >
                {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {t("cancelFlow.confirmCancel")}
              </button>
            </div>
          </div>
        )}

        {/* Step: Done */}
        {step === "done" && (
          <div className="p-6 text-center">
            <p className="text-white/60 text-2xl mb-3">😔</p>
            <h3 className="text-white font-semibold text-base mb-2">{t("cancelFlow.doneTitle")}</h3>
            <p className="text-white/40 text-sm mb-5">{t("cancelFlow.doneDesc")}</p>
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl text-sm font-medium bg-white/[0.08] text-white hover:bg-white/[0.12] transition-all"
            >
              {t("cancelFlow.close")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Add-ons definitions
// ═══════════════════════════════════════════════════════

interface AddonDef {
  id: string;
  name: string;
  description: string;
  price: number;
  badge?: string;
  icon: React.ReactNode;
}

type AddonCategory = {
  label: string;
  items: AddonDef[];
};

const ADDON_CATEGORIES: AddonCategory[] = [
  {
    label: "Tracking",
    items: [
      {
        id: "advance_tracking",
        name: "Advance Tracking",
        description: "Zaawansowany tracking i atrybucja kampanii",
        price: 299,
        icon: <Radar className="w-4 h-4" />,
        badge: "Addon",
      },
    ],
  },
];

function AddonCard({ addon }: { addon: AddonDef }) {
  const { t } = useTranslation("billingPanel");
  return (
    <div className="relative rounded-xl border border-white/[0.08] bg-white/[0.02] p-4 flex flex-col gap-3 hover:border-white/[0.15] hover:bg-white/[0.04] transition-all group">
      {addon.badge && (
        <span className="absolute -top-2.5 right-3 bg-[#ef3e29] text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
          {addon.badge}
        </span>
      )}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-white/60 group-hover:text-white/80 transition-colors">
          {addon.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-sm leading-tight">{addon.name}</p>
          <p className="text-white/40 text-xs mt-0.5 leading-snug">
            {t(`addons.${addon.id}.description`)}
          </p>
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <span className="text-white font-bold text-lg">{addon.price}</span>
          <span className="text-white/40 text-xs ml-1">{t("addons.priceSuffix")}</span>
        </div>
        <button
          className="flex items-center gap-1 text-xs font-medium text-[#ef3e29] hover:text-[#d63520] border border-[#ef3e29]/30 hover:border-[#ef3e29]/60 px-3 py-1.5 rounded-lg transition-all"
          onClick={() => window.open("https://gawronify.com/kontakt", "_blank")}
        >
          <Plus className="w-3 h-3" /> {t("addons.addButton")}
        </button>
      </div>
    </div>
  );
}

function AddonsSection() {
  const { t } = useTranslation("billingPanel");
  const [openCategory, setOpenCategory] = useState<string | null>("Pojedyncze funkcje");

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
      <div className="p-6 border-b border-white/[0.06]">
        <h3 className="text-white font-semibold text-base">{t("addons.title")}</h3>
        <p className="text-white/40 text-sm mt-1">{t("addons.desc")}</p>
      </div>
      <div className="divide-y divide-white/[0.06]">
        {ADDON_CATEGORIES.map((cat) => (
          <div key={cat.label}>
            <button
              className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-white/[0.02] transition-colors"
              onClick={() => setOpenCategory(openCategory === cat.label ? null : cat.label)}
            >
              <span className="text-white/70 font-medium text-sm">{cat.label}</span>
              <span className="text-white/30 text-xs">
                {openCategory === cat.label ? "▲" : "▼"}
              </span>
            </button>
            {openCategory === cat.label && (
              <div className="px-6 pb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {cat.items.map((addon) => (
                  <AddonCard key={addon.id} addon={addon} />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════

export default function BillingPanel() {
  const { user } = useAuth();
  const { t } = useTranslation("billingPanel");
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [cart, setCart] = useState<Map<Vertical, CartItem>>(new Map());
  const [packItem, setPackItem] = useState<PackItem | null>(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });

  // Subscription status from user data
  const subscriptionActive = (user as any)?.subscriptionActive ?? false;
  const tokensRemaining = (user as any)?.tokensRemaining ?? 0;
  const monthlyAllocation = (user as any)?.monthlyTokenAllocation ?? 3000;
  const trialEndsAt = (user as any)?.trialEndsAt;
  const isTrialActive = trialEndsAt ? new Date(trialEndsAt) > new Date() : false;

  const handleToggleSelect = useCallback(
    (vertical: Vertical, tier: Tier, tierName: string, price: number, tokens: number) => {
      setCart((prev) => {
        const next = new Map(prev);
        const existing = next.get(vertical);
        // If same tier already selected — remove it
        if (existing?.tier === tier) {
          next.delete(vertical);
          return next;
        }
        // Otherwise set (replaces any previous tier for this vertical)
        next.set(vertical, {
          vertical,
          verticalName: VERTICAL_NAMES[vertical],
          tier,
          tierName,
          price,
          tokens,
        });
        return next;
      });
      setCheckoutError(null);
    },
    []
  );

  const handleRemoveFromCart = useCallback((vertical: Vertical) => {
    setCart((prev) => {
      const next = new Map(prev);
      next.delete(vertical);
      return next;
    });
  }, []);

  const handleSelectPack = useCallback((pack: PackItem | null) => {
    setPackItem(pack);
    setCheckoutError(null);
  }, []);

  const handleRemovePack = useCallback(() => {
    setPackItem(null);
  }, []);

  async function handleCheckout() {
    if (cart.size === 0 && !packItem) return;

    setIsCheckingOut(true);
    setCheckoutError(null);
    try {
      const plans = Array.from(cart.values()).map((item) => ({
        vertical: item.vertical,
        tier: item.tier,
      }));

      const fpromTid = getFirstPromoterTid();

      // Single plan, no pack → use original endpoint for backwards compat
      if (plans.length === 1 && !packItem) {
        const res = await apiRequest("POST", "/api/billing/create-checkout", {
          vertical: plans[0].vertical,
          tier: plans[0].tier,
          cycle: billingCycle,
          ...(fpromTid ? { fpromTid } : {}),
        });
        if (res.url) window.location.href = res.url;
        return;
      }

      // Multiple plans and/or pack → multi-checkout
      const body: Record<string, unknown> = { cycle: billingCycle };
      if (plans.length > 0) body.plans = plans;
      if (packItem) body.adAccountPackPriceId = packItem.priceId;
      if (fpromTid) body.fpromTid = fpromTid;

      const res = await apiRequest("POST", "/api/billing/create-multi-checkout", body);
      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err) {
      // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
      console.error("Checkout error:", err);
      setCheckoutError(t("checkoutError"));
    } finally {
      setIsCheckingOut(false);
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative space-y-8"
      onMouseMove={(e) => setMousePosition({ x: e.clientX, y: e.clientY })}
      onMouseLeave={() => setMousePosition({ x: null, y: null })}
    >
      <InteractiveStarfield
        mousePosition={mousePosition}
        containerRef={containerRef as React.RefObject<HTMLDivElement>}
      />
      <div className="relative z-10 space-y-8">
        {/* Current status card */}
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6">
          <h2 className="text-white font-semibold text-base mb-4">{t("subscription.title")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Plan status */}
            <div className="bg-white/[0.04] rounded-lg p-4">
              <p className="text-white/40 text-xs mb-1">{t("subscription.status")}</p>
              {subscriptionActive ? (
                <div>
                  <span
                    className={`font-medium text-sm ${isCancelled ? "text-amber-400" : "text-emerald-400"}`}
                  >
                    {isCancelled
                      ? t("subscription.statusCancelled")
                      : t("subscription.statusActive")}
                  </span>
                </div>
              ) : isTrialActive ? (
                <div>
                  <span className="text-amber-400 font-medium text-sm">
                    {t("subscription.statusTrial")}
                  </span>
                  <p className="text-white/30 text-xs mt-0.5">
                    {t("subscription.statusTrialUntil", {
                      date: new Date(trialEndsAt).toLocaleDateString("pl-PL"),
                    })}
                  </p>
                </div>
              ) : (
                <span className="text-white/40 font-medium text-sm">
                  {t("subscription.statusNoPlan")}
                </span>
              )}
            </div>

            {/* Tokens */}
            <div className="bg-white/[0.04] rounded-lg p-4">
              <p className="text-white/40 text-xs mb-1">{t("subscription.tokens")}</p>
              <div className="flex items-baseline gap-1">
                <span
                  className={`font-medium text-sm ${tokensRemaining < 200 ? "text-red-400" : "text-white"}`}
                >
                  {tokensRemaining.toLocaleString("pl-PL")}
                </span>
                <span className="text-white/30 text-xs">
                  / {monthlyAllocation.toLocaleString("pl-PL")}
                </span>
              </div>
              <div className="mt-2 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#ef3e29] rounded-full transition-all"
                  style={{
                    width: `${Math.min((tokensRemaining / monthlyAllocation) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>

            {/* VP-256: dokup tokeny — tuż pod paskiem zużycia, tam gdzie klient ich szukał */}
            <TokenPackPurchase />

            {/* Quick action */}
            <div className="bg-white/[0.04] rounded-lg p-4 flex flex-col gap-2">
              <p className="text-white/40 text-xs">{t("subscription.management")}</p>
              {(user as any)?.stripeCustomerId ? (
                <>
                  <button
                    className="text-xs text-[#ef3e29] hover:text-[#d63520] font-medium flex items-center gap-1 transition-colors"
                    onClick={async () => {
                      try {
                        const res = await apiRequest("POST", "/api/billing/portal");
                        if (res.url) window.location.href = res.url;
                      } catch (err) {
                        // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
                        console.error("Portal error:", err);
                      }
                    }}
                  >
                    {t("subscription.paymentMethod")}
                    <Settings className="w-3 h-3" />
                  </button>
                  {subscriptionActive && !isCancelled && (
                    <button
                      className="text-xs text-white/30 hover:text-red-400 font-medium flex items-center gap-1 transition-colors"
                      onClick={() => setShowCancelModal(true)}
                    >
                      {t("subscription.cancelLink")}
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </>
              ) : (
                <p className="text-xs text-white/30">{t("subscription.selectPlan")}</p>
              )}
            </div>
          </div>
        </div>

        {/* Billing cycle toggle */}
        <BillingToggle billingCycle={billingCycle} onChange={setBillingCycle} />

        {/* Vertical sections */}
        {VERTICALS.map((vertical) => (
          <VerticalSection
            key={vertical.id}
            vertical={vertical}
            billingCycle={billingCycle}
            selectedTier={cart.get(vertical.id)?.tier ?? null}
            onToggleSelect={handleToggleSelect}
          />
        ))}

        {/* Ad account packs */}
        <AdAccountPacksSection selectedPack={packItem} onSelectPack={handleSelectPack} />

        {/* Add-ons & single features */}
        <AddonsSection />

        {/* Enterprise CTA */}
        <div className="rounded-xl border border-white/[0.08] bg-gradient-to-r from-white/[0.02] to-white/[0.04] p-6 text-center">
          <h3 className="text-white font-semibold text-lg mb-1">Enterprise</h3>
          <p className="text-white/40 text-sm mb-4">{t("enterprise.desc")}</p>
          <button
            className="bg-white/[0.08] hover:bg-white/[0.12] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all"
            onClick={() => window.open("https://gawronify.com/kontakt", "_blank")}
          >
            {t("enterprise.contact")}
          </button>
        </div>

        {/* FAQ note */}
        <p className="text-white/20 text-xs text-center">{t("footer")}</p>

        {/* Spacer so floating cart doesn't cover content */}
        {(cart.size > 0 || packItem) && <div className="h-20" />}

        {/* Floating cart bar */}
        <CartBar
          cart={cart}
          packItem={packItem}
          billingCycle={billingCycle}
          onRemove={handleRemoveFromCart}
          onRemovePack={handleRemovePack}
          onCheckout={handleCheckout}
          isLoading={isCheckingOut}
          checkoutError={checkoutError}
        />

        {/* Konfigurator — na samym dole: rekomendacja planu (Organic / Lead Gen / E-commerce) */}
        <PlanFinder />
      </div>

      {/* Cancel subscription modal */}
      {showCancelModal && (
        <CancelFlowModal
          onClose={() => setShowCancelModal(false)}
          onCancelled={() => setIsCancelled(true)}
        />
      )}
    </div>
  );
}
